import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SiteSettingsPanel from '../components/admin/SiteSettingsPanel';
import CustomPagesPanel from '../components/admin/CustomPagesPanel';
import IntegrationsPanel from '../components/admin/IntegrationsPanel';
import HomeBlocksPanel from '../components/admin/HomeBlocksPanel';
import TiersPanel from '../components/admin/TiersPanel';
import SellersPanel from '../components/admin/SellersPanel';
import PopupsPanel from '../components/admin/PopupsPanel';
import { Users, Plus, Edit2, Trash2, Search, X, Save, Shield, Settings, FileText, Zap, LayoutGrid, Award, CreditCard, Megaphone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import useMembershipTiers from '../hooks/useMembershipTiers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
    const { user } = useAuth();
    const { tiers: dynamicTiers } = useMembershipTiers();
    // Dropdown shape: [{value:'', label:'No Membership'}, ...]
    const tierOptions = [
        { value: '', label: 'No Membership' },
        ...dynamicTiers.map(t => ({ value: t.key, label: t.name }))
    ];
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('users'); // 'users' | 'settings' | 'pages'
    
    // Create/Edit user
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        membership_tier: '',
        membership_months: 1,
        membership_expires: '',
        role: 'member',
        can_multi_category: false
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchUsers();
        }
    }, [user]);

    const fetchUsers = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const submittingRef = React.useRef(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return; // hard guard against double-submit
        submittingRef.current = true;
        setSubmitting(true);
        
        try {
            if (editingUser) {
                const payload = {
                    name: formData.name,
                    membership_tier: formData.membership_tier || null,
                    role: formData.role,
                    password: formData.password || undefined,
                    can_multi_category: !!formData.can_multi_category,
                };
                // Send expiry only when a tier is set. Empty string clears the date.
                if (formData.membership_tier) {
                    payload.membership_expires = formData.membership_expires
                        ? new Date(formData.membership_expires + 'T23:59:59Z').toISOString()
                        : null;
                } else {
                    payload.membership_expires = null;
                }
                await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, payload, { withCredentials: true });
                toast.success('User updated successfully');
            } else {
                await axios.post(`${API_URL}/api/admin/users`, formData, { withCredentials: true });
                toast.success('User created successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save user');
        } finally {
            setSubmitting(false);
            submittingRef.current = false;
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        
        try {
            await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
            toast.success('User deleted');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    const openEditDialog = (userToEdit) => {
        setEditingUser(userToEdit);
        // Convert ISO date to YYYY-MM-DD for the <input type="date">
        const expiresDate = userToEdit.membership_expires
            ? new Date(userToEdit.membership_expires).toISOString().slice(0, 10)
            : '';
        setFormData({
            email: userToEdit.email,
            password: '',
            name: userToEdit.name || '',
            membership_tier: userToEdit.membership_tier || '',
            membership_months: 1,
            membership_expires: expiresDate,
            role: userToEdit.role || 'member',
            can_multi_category: !!userToEdit.can_multi_category
        });
        setDialogOpen(true);
    };

    const resetForm = () => {
        setEditingUser(null);
        setFormData({
            email: '',
            password: '',
            name: '',
            membership_tier: '',
            membership_months: 1,
            membership_expires: '',
            role: 'member',
            can_multi_category: false
        });
    };

    // Shift the currently selected expiry date by N days (used in edit dialog only)
    const shiftExpiry = (days) => {
        const base = formData.membership_expires
            ? new Date(formData.membership_expires + 'T12:00:00Z')
            : new Date();
        base.setUTCDate(base.getUTCDate() + days);
        setFormData({ ...formData, membership_expires: base.toISOString().slice(0, 10) });
    };

    const filteredUsers = users.filter(u =>
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTierBadgeClass = (tier) => {
        switch (tier) {
            case 'seeker': return 'tier-badge tier-seeker';
            case 'walker': return 'tier-badge tier-walker';
            case 'architect': return 'tier-badge tier-architect';
            case 'partnership': return 'tier-badge tier-partnership';
            default: return 'px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-sm';
        }
    };

    // Only admins can access this page
    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-[#F9F6F0]">
                <Navbar />
                <main className="pt-24 pb-16">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <Shield className="w-16 h-16 text-[#D8B4E2] mx-auto mb-4" />
                        <h1 className="heading-2 mb-4">Access Denied</h1>
                        <p className="body-text">This page is only accessible to administrators.</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="admin-page">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="mb-8">
                        <p className="overline mb-2">Administration</p>
                        <h1 className="heading-2">Control Center</h1>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mb-8 border-b border-[#E5E0D8] overflow-x-auto" data-testid="admin-tabs">
                        {[
                            { id: 'users', label: 'Users', Icon: Users },
                            { id: 'sellers', label: 'Sellers & Payouts', Icon: CreditCard },
                            { id: 'tiers', label: 'Tiers', Icon: Award },
                            { id: 'home', label: 'Homepage', Icon: LayoutGrid },
                            { id: 'popups', label: 'Pop-ups', Icon: Megaphone },
                            { id: 'settings', label: 'Site Settings', Icon: Settings },
                            { id: 'pages', label: 'Pages & Menu', Icon: FileText },
                            { id: 'integrations', label: 'Integrations', Icon: Zap }
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                className={`px-5 py-3 font-sans text-sm uppercase tracking-widest flex items-center gap-2 border-b-2 transition ${activeTab === t.id ? 'border-[#5B2D8E] text-[#5B2D8E]' : 'border-transparent text-[#4A4A68] hover:text-[#1A1A2E]'}`}
                                data-testid={`admin-tab-${t.id}`}
                            >
                                <t.Icon className="w-4 h-4" />
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'settings' && <SiteSettingsPanel />}
                    {activeTab === 'pages' && <CustomPagesPanel />}
                    {activeTab === 'integrations' && <IntegrationsPanel />}
                    {activeTab === 'home' && <HomeBlocksPanel />}
                    {activeTab === 'tiers' && <TiersPanel />}
                    {activeTab === 'sellers' && <SellersPanel />}
                    {activeTab === 'popups' && <PopupsPanel />}
                    {activeTab === 'users' && (
                    <>
                    {/* Users sub-header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                        <h2 className="font-serif text-3xl text-[#1A1A2E]">User Management</h2>
                        
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <button className="btn-primary flex items-center gap-2" data-testid="create-user-btn">
                                    <Plus className="w-4 h-4" />
                                    Create User
                                </button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md bg-white">
                                <DialogHeader>
                                    <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                                        {editingUser ? 'Edit User' : 'Create New User'}
                                    </DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Email *</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full"
                                            required
                                            disabled={!!editingUser}
                                            data-testid="user-email"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                            {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                                        </label>
                                        <input
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            className="w-full"
                                            required={!editingUser}
                                            data-testid="user-password"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Name *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full"
                                            required
                                            data-testid="user-name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Role</label>
                                        <select
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                            data-testid="user-role"
                                        >
                                            <option value="member">Member</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                    {editingUser && (
                                        <label className="flex items-start gap-3 cursor-pointer p-3 bg-[#F9F6F0] rounded-sm">
                                            <input
                                                type="checkbox"
                                                checked={!!formData.can_multi_category}
                                                onChange={(e) => setFormData({ ...formData, can_multi_category: e.target.checked })}
                                                className="mt-1"
                                                data-testid="user-can-multi-category"
                                            />
                                            <div className="flex-1">
                                                <p className="text-sm text-[#1A1A2E]"><strong>Allow multiple readiness categories</strong></p>
                                                <p className="text-xs text-[#4A4A68] mt-1">
                                                    When on, this pillar can list themselves in two or three of the A / B / C audience readiness categories. Admins are already allowed by default.
                                                </p>
                                            </div>
                                        </label>
                                    )}
                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Membership Tier</label>
                                        <select
                                            value={formData.membership_tier}
                                            onChange={(e) => setFormData({ ...formData, membership_tier: e.target.value })}
                                            className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                            data-testid="user-tier"
                                        >
                                            {tierOptions.map(tier => (
                                                <option key={tier.value} value={tier.value}>{tier.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {!editingUser && formData.membership_tier && (
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Membership Duration (months)</label>
                                            <input
                                                type="number"
                                                value={formData.membership_months}
                                                onChange={(e) => setFormData({ ...formData, membership_months: parseInt(e.target.value) || 1 })}
                                                className="w-full"
                                                min="1"
                                                data-testid="user-months"
                                            />
                                        </div>
                                    )}
                                    {editingUser && formData.membership_tier && (
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Membership End Date</label>
                                            <input
                                                type="date"
                                                value={formData.membership_expires}
                                                onChange={(e) => setFormData({ ...formData, membership_expires: e.target.value })}
                                                className="w-full"
                                                data-testid="user-expires"
                                            />
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => shiftExpiry(30)}
                                                    className="px-3 py-1 text-xs border border-[#5B2D8E] text-[#5B2D8E] rounded-sm hover:bg-[#5B2D8E] hover:text-white transition"
                                                    data-testid="extend-30d-btn"
                                                >
                                                    + 30 days
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => shiftExpiry(90)}
                                                    className="px-3 py-1 text-xs border border-[#5B2D8E] text-[#5B2D8E] rounded-sm hover:bg-[#5B2D8E] hover:text-white transition"
                                                    data-testid="extend-90d-btn"
                                                >
                                                    + 90 days
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => shiftExpiry(365)}
                                                    className="px-3 py-1 text-xs border border-[#5B2D8E] text-[#5B2D8E] rounded-sm hover:bg-[#5B2D8E] hover:text-white transition"
                                                    data-testid="extend-365d-btn"
                                                >
                                                    + 1 year
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, membership_expires: '' })}
                                                    className="px-3 py-1 text-xs border border-[#E5E0D8] text-[#4A4A68] rounded-sm hover:bg-[#F2EBE5] transition"
                                                    data-testid="clear-expires-btn"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            <p className="text-xs text-[#4A4A68] mt-2">Leave empty for lifetime / no expiry.</p>
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn-primary w-full"
                                        disabled={submitting}
                                        data-testid="save-user-btn"
                                    >
                                        {submitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                                    </button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {/* Search */}
                    <div className="relative mb-6">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A68]" />
                        <input
                            type="text"
                            placeholder="Search users by email or name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12"
                            data-testid="user-search"
                        />
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                        <div className="bg-white border border-[#E5E0D8] p-4 rounded-sm text-center">
                            <p className="font-serif text-2xl text-[#1A1A2E]">{users.length}</p>
                            <p className="text-xs text-[#4A4A68]">Total Users</p>
                        </div>
                        {dynamicTiers.map(tier => (
                            <div key={tier.key} className="bg-white border border-[#E5E0D8] p-4 rounded-sm text-center">
                                <p className="font-serif text-2xl text-[#1A1A2E]">{users.filter(u => u.membership_tier === tier.key).length}</p>
                                <p className="text-xs text-[#4A4A68]">{tier.name}</p>
                            </div>
                        ))}
                    </div>

                    {/* Users Table */}
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-[#4A4A68]">Loading users...</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full" data-testid="users-table">
                                    <thead className="bg-[#F9F6F0]">
                                        <tr>
                                            <th className="text-left p-4 font-sans text-sm text-[#4A4A68]">User</th>
                                            <th className="text-left p-4 font-sans text-sm text-[#4A4A68]">Role</th>
                                            <th className="text-left p-4 font-sans text-sm text-[#4A4A68]">Membership</th>
                                            <th className="text-left p-4 font-sans text-sm text-[#4A4A68]">Expires</th>
                                            <th className="text-left p-4 font-sans text-sm text-[#4A4A68]">Joined</th>
                                            <th className="text-right p-4 font-sans text-sm text-[#4A4A68]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map(u => (
                                            <tr key={u.id} className="border-t border-[#E5E0D8] hover:bg-[#F9F6F0]">
                                                <td className="p-4">
                                                    <p className="font-serif text-[#1A1A2E]">{u.name || 'No name'}</p>
                                                    <p className="text-sm text-[#4A4A68]">{u.email}</p>
                                                </td>
                                                <td className="p-4">
                                                    {u.role === 'admin' ? (
                                                        <span className="px-2 py-1 bg-[#5B2D8E] text-white text-xs rounded-sm">Admin</span>
                                                    ) : (
                                                        <span className="text-sm text-[#4A4A68]">Member</span>
                                                    )}
                                                </td>
                                                <td className="p-4">
                                                    {u.membership_tier ? (
                                                        <span className={getTierBadgeClass(u.membership_tier)}>
                                                            {u.membership_tier}
                                                        </span>
                                                    ) : (
                                                        <span className="text-sm text-[#4A4A68]">None</span>
                                                    )}
                                                </td>
                                                <td className="p-4 text-sm text-[#4A4A68]">
                                                    {u.membership_expires ? new Date(u.membership_expires).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="p-4 text-sm text-[#4A4A68]">
                                                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="p-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => openEditDialog(u)}
                                                            className="p-2 hover:bg-[#F2EBE5] rounded"
                                                            data-testid={`edit-user-${u.id}`}
                                                        >
                                                            <Edit2 className="w-4 h-4 text-[#4A4A68]" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(u.id)}
                                                            className="p-2 hover:bg-red-50 rounded"
                                                            disabled={u.id === user?.id}
                                                            data-testid={`delete-user-${u.id}`}
                                                        >
                                                            <Trash2 className={`w-4 h-4 ${u.id === user?.id ? 'text-gray-300' : 'text-red-500'}`} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
